import 'twin.macro';
import 'styled-components/macro';

import { useFormik } from 'formik';
import { HTMLAttributes } from 'react';
import { BooleanSchema, NumberSchema, StringSchema, boolean, number, string } from 'yup';

import { Input } from 'component-lib';
import ConsentLabel from 'component-lib/atoms/consent-label/ConsentLabel';
import { Checkbox } from 'component-lib/atoms/controls/Checkbox';
import { Form } from 'component-lib/atoms/controls/Form';
import { SelectItem } from 'component-lib/atoms/select/SelectItem';
import { FormSelect } from 'component-lib/molecules/form/Select';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import { FormField, FormFieldName, UserDetailsInput } from 'types/graphql';

import { countries } from './countries';
import { jobLevels } from './job-levels';
import { states } from './us-states';

export const labelsByFieldName: Record<FormFieldName, string> = {
  [FormFieldName.FirstName]: 'First name',
  [FormFieldName.LastName]: 'Last name',
  [FormFieldName.Email]: 'Email address',
  [FormFieldName.CompanyName]: 'Company name',
  [FormFieldName.PhoneNumber]: 'Phone number',
  [FormFieldName.JobTitle]: 'Job title',
  [FormFieldName.JobLevel]: 'Job level',
  [FormFieldName.CountryCode]: 'Country',
  [FormFieldName.UsState]: 'US State',
  [FormFieldName.Consent]: 'Rules',
};

export const fieldNameToInputMap: Record<FormFieldName, string> = {
  [FormFieldName.FirstName]: 'firstName',
  [FormFieldName.LastName]: 'lastName',
  [FormFieldName.Email]: 'email',
  [FormFieldName.CompanyName]: 'companyName',
  [FormFieldName.PhoneNumber]: 'phoneNumber',
  [FormFieldName.JobTitle]: 'jobTitle',
  [FormFieldName.JobLevel]: 'jobLevel',
  [FormFieldName.CountryCode]: 'countryCode',
  [FormFieldName.UsState]: 'usState',
  [FormFieldName.Consent]: 'consent',
};

export interface UserDetailsFormFields extends Record<Exclude<FormFieldName, FormFieldName.Consent>, string> {
  [FormFieldName.Consent]: boolean;
  privacyPolicyConsent?: boolean;
}

export interface UserDetailsFormProps extends Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  fields: FormField[];
  consentNotice?: string;
  validateWorkEmail?: boolean;
  onSubmit: (formData: UserDetailsInput, privacyPolicyConsent?: boolean) => void;
  privacyPolicyURL?: string;
  teamName?: string;
  setHasError?: (value: boolean) => void; // sets to true/false depending on Formik errors
}

export function UserDetailsForm({
  fields,
  consentNotice,
  validateWorkEmail,
  onSubmit,
  privacyPolicyURL,
  teamName,
  setHasError,
  ...props
}: UserDetailsFormProps) {
  const trackAmplitudeEvent = useTrackAmplitudeEvent();

  const { getFieldProps, getFieldMeta, handleSubmit, values, errors, validateForm } = useFormik<UserDetailsFormFields>({
    initialValues: {
      ...fields.reduce((accumulator, { fieldName }) => {
        return {
          ...accumulator,
          [fieldName]: fieldName === FormFieldName.Consent ? false : '',
        };
      }, {} as UserDetailsFormFields),
      ...(privacyPolicyURL && { privacyPolicyConsent: false }),
    },
    onSubmit: formData => {
      const { privacyPolicyConsent, ...data } = formData;

      return onSubmit(mapToInput(data), privacyPolicyConsent);
    },
    validate: generateValidations(fields),
  });

  const fieldsToShow = new Map(fields.map(f => [f.fieldName, f]));

  return (
    <form
      onChange={() => {
        if (Object.values(errors).length === 0) setHasError?.(false);
      }}
      onSubmit={async e => {
        e.preventDefault();

        const errs = Object.keys(await validateForm(values)).length;

        if (errs) {
          trackAmplitudeEvent({
            name: 'Track Invite overview - Form submitted unsuccessfully',
            data: {
              formFieldsCount: fields.length,
            },
          });
        }

        setHasError?.(!!errs);
        handleSubmit(e);
      }}
      tw="flex flex-col"
      {...props}
    >
      <Form.Group>
        <div tw="flex gap-2">
          {fieldsToShow.has(FormFieldName.FirstName) && (
            <Input
              label={createFieldLabel(FormFieldName.FirstName, fieldsToShow)}
              tw="flex-1"
              placeholder="First name"
              {...getFieldProps(FormFieldName.FirstName)}
              {...getFieldMeta(FormFieldName.FirstName)}
            />
          )}
          {fieldsToShow.has(FormFieldName.LastName) && (
            <Input
              label={createFieldLabel(FormFieldName.LastName, fieldsToShow)}
              tw="flex-1"
              placeholder="Last name"
              {...getFieldProps(FormFieldName.LastName)}
              {...getFieldMeta(FormFieldName.LastName)}
            />
          )}
        </div>
      </Form.Group>

      {fieldsToShow.has(FormFieldName.Email) && (
        <Input
          label={
            validateWorkEmail
              ? `Business email${fieldsToShow.get(FormFieldName.Email)?.isRequired ? '*' : ''}`
              : createFieldLabel(FormFieldName.Email, fieldsToShow)
          }
          placeholder="Email address"
          {...getFieldProps(FormFieldName.Email)}
          {...getFieldMeta(FormFieldName.Email)}
        />
      )}

      {fieldsToShow.has(FormFieldName.CompanyName) && (
        <Input
          label={createFieldLabel(FormFieldName.CompanyName, fieldsToShow)}
          placeholder="Company name"
          {...getFieldProps(FormFieldName.CompanyName)}
          {...getFieldMeta(FormFieldName.CompanyName)}
        />
      )}

      {fieldsToShow.has(FormFieldName.PhoneNumber) && (
        <Input
          label={createFieldLabel(FormFieldName.PhoneNumber, fieldsToShow)}
          placeholder="Phone number"
          {...getFieldProps(FormFieldName.PhoneNumber)}
          {...getFieldMeta(FormFieldName.PhoneNumber)}
        />
      )}

      {fieldsToShow.has(FormFieldName.JobTitle) && (
        <Input
          label={createFieldLabel(FormFieldName.JobTitle, fieldsToShow)}
          placeholder="Job title"
          {...getFieldProps(FormFieldName.JobTitle)}
          {...getFieldMeta(FormFieldName.JobTitle)}
        />
      )}

      {fieldsToShow.has(FormFieldName.JobLevel) && (
        <FormSelect
          label={createFieldLabel(FormFieldName.JobLevel, fieldsToShow)}
          placeholder="Select job level"
          {...getFieldProps(FormFieldName.JobLevel)}
          {...getFieldMeta(FormFieldName.JobLevel)}
        >
          {jobLevels.map(jobLevel => (
            <SelectItem key={jobLevel} value={jobLevel}>
              {jobLevel}
            </SelectItem>
          ))}
        </FormSelect>
      )}

      {fieldsToShow.has(FormFieldName.CountryCode) && (
        <FormSelect
          label={createFieldLabel(FormFieldName.CountryCode, fieldsToShow)}
          placeholder="Select country"
          {...getFieldProps(FormFieldName.CountryCode)}
          {...getFieldMeta(FormFieldName.CountryCode)}
        >
          {countries.map(option => (
            <SelectItem key={option['alpha-3']} value={option['alpha-3']}>
              {option.name}
            </SelectItem>
          ))}
        </FormSelect>
      )}

      {fieldsToShow.has(FormFieldName.UsState) && getFieldProps(FormFieldName.CountryCode).value === 'USA' && (
        <FormSelect
          label={createFieldLabel(FormFieldName.UsState, fieldsToShow)}
          placeholder="Select state"
          {...getFieldProps(FormFieldName.UsState)}
          {...getFieldMeta(FormFieldName.UsState)}
        >
          {states.map(option => (
            <SelectItem key={option.iso_code} value={option.iso_code}>
              {option.name}
            </SelectItem>
          ))}
        </FormSelect>
      )}

      {fieldsToShow.has(FormFieldName.Consent) && (
        <>
          <Checkbox
            data-testid="consent"
            label={consentNotice && <ConsentLabel children={consentNotice} />}
            {...getFieldProps(FormFieldName.Consent)}
            {...getFieldMeta(FormFieldName.Consent)}
          />
          {getFieldMeta(FormFieldName.Consent).touched && errors[FormFieldName.Consent] && (
            <Form.Hint error>{errors[FormFieldName.Consent]}</Form.Hint>
          )}
        </>
      )}

      {privacyPolicyURL && (
        <Checkbox
          label={
            <ConsentLabel
              children={`I agree to receive digital communication, including marketing about **${teamName}**'s products and services. For more information read ${teamName}'s [privacy policy](${privacyPolicyURL}).`}
            />
          }
          {...getFieldProps('privacyPolicyConsent')}
          {...getFieldMeta('privacyPolicyConsent')}
        />
      )}
    </form>
  );
}

const generateValidations = (fields: UserDetailsFormProps['fields']) => async (formikFields: UserDetailsFormFields) => {
  const errors: Partial<Record<string, string>> = {};

  for (const field in formikFields) {
    const isRequired = !!fields.find(({ fieldName }) => field === fieldName)?.isRequired;

    try {
      switch (field) {
        case FormFieldName.Email:
          await getIsRequired(isRequired, string().email().label(labelsByFieldName[FormFieldName.Email])).validate(
            formikFields[field],
          );
          break;
        case FormFieldName.Consent:
          await getIsRequired(
            isRequired,
            boolean()
              .label(labelsByFieldName[FormFieldName.Consent])
              .oneOf([true], 'Your consent is required to access this content.'),
          ).validate(formikFields[field]);
          break;
        case FormFieldName.PhoneNumber:
          await getIsRequired(
            isRequired,
            number()
              .nullable()
              .transform((numberValue: number, val: string) => (!isRequired && val.length === 0 ? null : numberValue))
              .typeError(`${labelsByFieldName[FormFieldName.PhoneNumber]} should consist of only numbers`)
              .label(labelsByFieldName[FormFieldName.PhoneNumber]),
          ).validate(formikFields[field]);
          break;
        case FormFieldName.UsState:
          await getIsRequired(formikFields[FormFieldName.CountryCode] === 'USA' && isRequired, string()).validate(
            formikFields[field],
          );
          break;
        default:
          await getIsRequired(isRequired, string().label(labelsByFieldName[field as FormFieldName])).validate(
            formikFields[field as FormFieldName],
          );
      }
    } catch (e) {
      errors[field] = (e as Error).message;
    }
  }

  return errors;
};

const getIsRequired = (
  isRequired: boolean,
  validation: StringSchema | BooleanSchema | NumberSchema<number | null | undefined>,
) => (!isRequired ? validation : validation.required());

const createFieldLabel = (fieldName: FormFieldName, fields: Map<FormFieldName, FormField>) =>
  `${labelsByFieldName[fieldName]}${fields.get(fieldName)?.isRequired ? '*' : ''}`;

export const mapToInput = (formFields: Partial<UserDetailsFormFields>): UserDetailsInput => {
  return Object.keys(formFields).reduce((accumulator, next) => {
    const key = fieldNameToInputMap[next as FormFieldName];
    if (!key) {
      return accumulator;
    }

    return {
      ...accumulator,
      [key]: formFields[next as FormFieldName],
    };
  }, {} as UserDetailsInput);
};
